import { useLazyQuery, useQuery } from '@apollo/client';
import { AutoFixHigh } from '@mui/icons-material';
import { Box } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMoveDesign } from '~/api/designs';
import { FilterSidebar, FilterToggle } from '~/components/Filter';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { ViewToggle } from '~/components/ViewToggle';
import { Count } from '~/components/count';
import { MoveToFolderDialog } from '~/components/dialogs/MoveToFolderDialog';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext, useFolderContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import {
  BulkActions,
  BulkTagDialog,
  DesignMoveDocument,
  FilterAccordion,
  FilterChips,
  StudioGrid,
  StudioTable,
} from '../list/components';
import {
  DesignFiltersProvider,
  useDesignFiltersParams,
  useDesignFiltersReducer,
} from '../list/context';
import { DesignSearchDocument } from './search.generated';

export const DesignSearch = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const { folderTree } = useFolderContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const backToFolder = searchParams.get('backToFolder')?.trim() ?? '';
  const view = searchParams.get('view')?.trim() || 'Grid';

  const link = useLink();
  const navigate = useNavigate();

  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const [tagDesignDialogOpen, setTagDesignDialogOpen] = useState(false);
  const [moveDesignDialogOpen, setMoveDesignDialogOpen] = useState(false);
  const [moveDesign, moveDesignInfo] = useMoveDesign();

  const filterParams = useDesignFiltersParams();
  const tags = filterParams.tags;
  const published = filterParams.published;

  const [searchDesigns, { data, loading }] = useLazyQuery(DesignSearchDocument);

  const { data: designMoveData } = useQuery(DesignMoveDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !moveDesignDialogOpen,
  });
  const editableFolderIds = useMemo(
    () =>
      designMoveData?.network?.designFolders.filter((x) => x.canUpdate.value).map((x) => x.id) ??
      [],
    [designMoveData],
  );

  useEffect(() => {
    if (!search) return;
    void searchDesigns({
      variables: {
        networkId: currentNetwork.id,
        page,
        perPage,
        search,
        tags,
        published,
      },
    });
  }, [currentNetwork.id, page, perPage, search, searchDesigns, tags, published]);

  const pageInfo = data?.network?.designs.pageInfo;
  useEffect(() => {
    if (pageInfo) {
      handleOutOfBounds(pageInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo]);

  const totalCount = useTotalCount(data?.network?.designs.pageInfo.nodesCount);
  const designs = useMemo(() => data?.network?.designs.nodes ?? [], [data]);

  const designTags = useMemo(
    () => data?.network?.designTags.map((x) => x.name),
    [data?.network?.designTags],
  );
  const filterCounts = data?.network?.designFilterInfo;

  const [designFiltersState, designFiltersDispatch] = useDesignFiltersReducer(
    filterParams,
    designTags,
  );

  const filtersActive = useMemo(() => tags.length > 0 || published.length > 0, [tags, published]);

  const canDestroySelectedDesigns = selectedIds.every((selectedId) => {
    const design = designs.find((design) => design.id === selectedId);
    return design?.canDestroy.value;
  });

  const handleClearFilter = useCallback(() => {
    const folderId = backToFolder || folderTree.id;
    navigate(link(`/studio/${folderId}`));
  }, [backToFolder, folderTree.id, link, navigate]);

  const handleSearch = useCallback(
    (newSearch: string) => {
      if (newSearch === '') {
        handleClearFilter();
        return;
      }
      setSearchParams((params) => {
        params.set('search', newSearch);
        params.set('backToFolder', backToFolder);
        return params;
      });
    },
    [backToFolder, handleClearFilter, setSearchParams],
  );

  return (
    <DesignFiltersProvider
      {...designFiltersState}
      clearFilters={() => designFiltersDispatch({ type: 'CLEAR_FILTERS' })}
      reinitialize={(params) => designFiltersDispatch({ type: 'REINITIALIZE', params })}
      setFilter={(value) => designFiltersDispatch({ type: 'SET_FILTER', ...value })}
      toggleFilter={(field) => designFiltersDispatch({ type: 'TOGGLE_FILTER', field })}
      togglePanel={() => designFiltersDispatch({ type: 'TOGGLE_PANEL' })}
    >
      <Helmet title={t('pages.studio.search.title')} />
      <Toolbar titleIcon={<AutoFixHigh />} titleText={t('pages.studio.search.title')} />

      <ListPageContainer>
        <FilterSidebar open={designFiltersState.panel.open} persistAs="designSearch:filterWidth">
          <FilterAccordion filterCounts={filterCounts} />
        </FilterSidebar>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FilterToggle
                  active={filtersActive}
                  onToggle={() => designFiltersDispatch({ type: 'TOGGLE_PANEL' })}
                />
                <SearchBar
                  onChange={handleSearch}
                  placeholder={t('pages.studio.search_placeholder')}
                  search={search}
                />
              </Box>
              <Box sx={{ display: { md: 'flex', xs: 'none' }, alignItems: 'center' }}>
                {selectedIds.length > 0 && (
                  <BulkActions
                    selectedIds={selectedIds}
                    onTagDesign={() => setTagDesignDialogOpen(true)}
                    onMoveDesign={() => setMoveDesignDialogOpen(true)}
                    canDestroySelectedDesigns={canDestroySelectedDesigns}
                  />
                )}
                <Box sx={{ display: 'flex' }}>
                  <Count
                    selectedCount={selectedIds.length}
                    totalCount={totalCount || 0}
                    thing="pages.studio.thing_plural"
                  />
                </Box>
              </Box>
            </ListActions>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  visibility: filtersActive ? 'visible' : 'hidden',
                }}
              >
                <FilterChips />
              </Box>
              <ViewToggle
                view={view as 'List' | 'Grid'}
                onViewChange={(newView) =>
                  setSearchParams((prev) => ({ ...Object.fromEntries(prev), view: newView }))
                }
              />
            </Box>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {view === 'List' ? (
                  <StudioTable
                    designs={designs}
                    folders={[]}
                    loading={loading}
                    onCheck={setSelectedIds}
                    selectedIds={selectedIds}
                    search
                  />
                ) : (
                  <StudioGrid
                    designs={designs}
                    folders={[]}
                    onCheck={setSelectedIds}
                    selectedIds={selectedIds}
                  />
                )}
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>

          <MoveToFolderDialog
            editableFolderIds={editableFolderIds}
            onSubmit={async (targetFolderId) => {
              await moveDesign({
                refetchQueries: ['DesignList', 'DesignSearch'],
                variables: {
                  designFolderId: targetFolderId,
                  designIds: selectedIds as number[],
                },
              });
              setSelectedIds([]);
            }}
            loading={moveDesignInfo.loading}
            onClose={() => setMoveDesignDialogOpen(false)}
            open={moveDesignDialogOpen}
            i18nKey="pages.studio.dialogs.move_design.title"
          />

          {tagDesignDialogOpen && (
            <BulkTagDialog
              designIds={selectedIds as string[]}
              designTags={data?.network?.designTags.map((x) => x.name) || []}
              onClose={() => setTagDesignDialogOpen(false)}
              onComplete={() => setSelectedIds(() => [])}
              open
              tags={tags}
            />
          )}
        </ListContainer>
      </ListPageContainer>
    </DesignFiltersProvider>
  );
};
